import { CalendarDays, Factory, Info, ListTodo, Mail, ReceiptText } from "lucide-react";
import React from "react";
import { FormattedNumber } from "react-intl";
import { useGetV4WarehousesQuery } from "randmar-api-client";
import { PurchaseOrder } from "../../stores/document/Document";
import { Alert, AlertDescription, AlertTitle, Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "randmarcomps";
import CommentsView from "../comments/CommentsView";
import ReceiptsGrid from '../grids/ReceiptsGrid';
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import PurchaseOrderPartNumberRow from "./PurchaseOrderPartNumberRow";
import EditExpectedDate from "./actions/EditExpectedDate";

interface IProps {
  purchaseOrder: PurchaseOrder;
}

const PurchaseOrderView: React.FC<IProps> = ({ purchaseOrder }) => {
  const { data: warehouses } = useGetV4WarehousesQuery();

  const getWarehouseNameFromCode = React.useCallback((code?: string) => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  return (
    <>
      <section className="space-y-6" aria-label="Purchase order details">
        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4">
              <header className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Purchase order details</p>
                <h2 className="text-[24px] font-semibold text-slate-900">Overview</h2>
              </header>

              <Alert className="rounded-2xl border border-sky-100 bg-sky-50 shadow-sm">
                <Info className="h-4 w-4 text-sky-600" aria-hidden="true" />
                <AlertTitle className="text-slate-900">Editing is available</AlertTitle>
                <AlertDescription className="text-slate-700">
                  Use the Actions menu to update this purchase order (quantity, pricing, expected arrival date, and more).
                </AlertDescription>
              </Alert>

              <dl className="grid gap-2 sm:grid-cols-2" aria-label="Purchase order metadata">
                <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
                  <CalendarDays className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Purchase order date</dt>
                    <dd className="text-[15px] text-slate-700">{FormatDateFromInt(purchaseOrder.purchaseOrderDate)}</dd>
                  </div>
                </div>

                <div className="flex items-start justify-between gap-3 rounded-xl p-3 hover:bg-gray-50">
                  <div className="flex items-start gap-3">
                    <CalendarDays className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Expected arrival</dt>
                      <dd className="text-[15px] text-slate-700">{FormatDateFromInt(purchaseOrder.expectedArrivalDate)}</dd>
                    </div>
                  </div>
                  <EditExpectedDate
                    expectedArrivalDate={purchaseOrder.expectedArrivalDate || 0}
                    purchaseOrderNumber={purchaseOrder.purchaseOrderNumber || ""}
                  />
                </div>

                <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
                  <Factory className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Location</dt>
                    <dd className="text-[15px] text-slate-700">
                      {getWarehouseNameFromCode(purchaseOrder.location)}
                    </dd>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
                  <ReceiptText className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subtotal</dt>
                    <dd className="text-[15px] font-medium text-slate-900">
                      <FormattedNumber {...CurrencyUtils.format} value={purchaseOrder.subTotal || 0} />
                    </dd>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
                  <ListTodo className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Terms</dt>
                    <dd className="text-[15px] text-slate-700">{purchaseOrder.termsCode || "—"}</dd>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
                  <Mail className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contact email</dt>
                    <dd className="text-[15px] text-slate-700">
                      {purchaseOrder.contactEmail && purchaseOrder.contactEmail.trim() !== ""
                        ? purchaseOrder.contactEmail
                        : <span className="italic text-slate-500">Undefined</span>}
                    </dd>
                  </div>
                </div>
              </dl>
            </div>
          </Card>

          <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="space-y-3">
              <header className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">History</p>
                <h2 className="text-[24px] font-semibold text-slate-900">Comments</h2>
              </header>
              <CommentsView comments={purchaseOrder.comments || ""} />
            </div>
          </Card>
        </div>

        {purchaseOrder.receipts?.length ? (
          <ReceiptsGrid receipts={purchaseOrder.receipts} />
        ) : null}

        <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-[20px] font-medium text-slate-900">
                Line items
              </h3>
              <p className="text-[15px] text-slate-600">
                Review and adjust quantities or unit pricing.
              </p>
            </div>
            <p className="text-sm text-slate-500">
              {(purchaseOrder.partNumbers ?? []).length} line{(purchaseOrder.partNumbers ?? []).length === 1 ? "" : "s"}
            </p>
          </div>

          <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow className="h-14 hover:bg-gray-50">
                  <TableHead className="min-w-[240px] text-xs font-semibold uppercase tracking-wide text-slate-500">Product</TableHead>
                  <TableHead className="w-[140px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity</TableHead>
                  <TableHead className="w-[140px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Update</TableHead>
                  <TableHead className="w-[160px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Unit price</TableHead>
                  <TableHead className="w-[160px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Extended</TableHead>
                  <TableHead className="w-[120px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Details</TableHead>
                  <TableHead className="w-[120px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(purchaseOrder.partNumbers ?? [])
                  .slice()
                  .sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0))
                  .map((row, index) => (
                    <PurchaseOrderPartNumberRow
                      key={`${index}-POPartNumber-${row.mpn ?? row.randmarSKU ?? "unknown"}`}
                      purchaseOrderNumber={purchaseOrder.purchaseOrderNumber || ""}
                      partNumber={row}
                    />
                  ))}

                {!purchaseOrder.partNumbers?.length ? (
                  <TableRow className="h-14">
                    <TableCell colSpan={7} className="text-center text-sm text-slate-500">
                      This purchase order has no line items.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </Card>
      </section>
    </>
  );
};

export default PurchaseOrderView;
